// ColorInput.tsx
// Dynamic input panel for creating and editing custom color palettes

import React, { useState } from 'react';
import { PaletteStructure } from './types';

interface ColorInputProps {
  paletteStructure: PaletteStructure;
  onPaletteChange: (structure: PaletteStructure) => void;
  onInitialize: () => void;
}

export const ColorInput: React.FC<ColorInputProps> = ({ 
  paletteStructure, 
  onPaletteChange, 
  onInitialize 
}) => {
  const [newGroupName, setNewGroupName] = useState('');
  const [newColorKey, setNewColorKey] = useState('');
  const [newColorValue, setNewColorValue] = useState('#000000');
  const [selectedGroup, setSelectedGroup] = useState(0);

  const handleColorChange = (key: string, value: string) => {
    onPaletteChange({
      ...paletteStructure,
      colors: {
        ...paletteStructure.colors,
        [key]: value
      }
    });
  };

  const handleColorKeyRename = (oldKey: string, newKey: string) => {
    if (newKey === oldKey || !newKey || paletteStructure.colors[newKey]) return;
    
    const newColors = { ...paletteStructure.colors };
    newColors[newKey] = newColors[oldKey];
    delete newColors[oldKey];

    const newGroups = paletteStructure.groups.map(group => ({
      ...group,
      colors: group.colors.map(c => c === oldKey ? newKey : c)
    }));

    onPaletteChange({
      colors: newColors,
      groups: newGroups
    });
  };

  const handleAddColor = () => {
    if (!newColorKey || paletteStructure.colors[newColorKey]) return;
    
    const newColors = {
      ...paletteStructure.colors,
      [newColorKey]: newColorValue
    };

    const newGroups = [...paletteStructure.groups];
    if (newGroups[selectedGroup]) {
      newGroups[selectedGroup] = {
        ...newGroups[selectedGroup],
        colors: [...newGroups[selectedGroup].colors, newColorKey]
      };
    }

    onPaletteChange({
      colors: newColors,
      groups: newGroups
    });

    setNewColorKey('');
    setNewColorValue('#000000');
  };

  const handleRemoveColor = (key: string) => {
    const newColors = { ...paletteStructure.colors };
    delete newColors[key];

    const newGroups = paletteStructure.groups.map(group => ({
      ...group,
      colors: group.colors.filter(c => c !== key)
    }));

    onPaletteChange({
      colors: newColors,
      groups: newGroups
    });
  };

  const handleAddGroup = () => {
    if (!newGroupName) return;
    
    onPaletteChange({
      ...paletteStructure,
      groups: [
        ...paletteStructure.groups,
        { title: newGroupName, colors: [] }
      ]
    });
    setNewGroupName('');
  };

  const handleRemoveGroup = (index: number) => {
    const newGroups = paletteStructure.groups.filter((_, i) => i !== index);
    onPaletteChange({
      ...paletteStructure,
      groups: newGroups
    });
  };

  const handleRenameGroup = (index: number, newTitle: string) => {
    const newGroups = [...paletteStructure.groups];
    newGroups[index] = { ...newGroups[index], title: newTitle };
    onPaletteChange({
      ...paletteStructure,
      groups: newGroups
    });
  };

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold mb-4">Input Color Palette</h2>
      <p className="text-sm text-gray-400 mb-4">
        Create your custom color palette. Add groups and colors, then click "Initialize Sphere".
      </p>

      {/* Add New Group */}
      <div className="bg-gray-700 p-3 rounded">
        <h3 className="text-sm font-semibold mb-2 text-blue-400">Add New Group</h3>
        <div className="flex gap-2">
          <input
            type="text"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            placeholder="Group name"
            className="flex-1 px-2 py-1 bg-gray-600 rounded text-sm"
          />
          <button
            onClick={handleAddGroup}
            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm"
          >
            Add Group
          </button>
        </div>
      </div>

      {/* Add New Color */}
      <div className="bg-gray-700 p-3 rounded">
        <h3 className="text-sm font-semibold mb-2 text-green-400">Add New Color</h3>
        <div className="space-y-2">
          <input
            type="text"
            value={newColorKey}
            onChange={(e) => setNewColorKey(e.target.value.replace(/\s/g, ''))}
            placeholder="colorKey (no spaces)"
            className="w-full px-2 py-1 bg-gray-600 rounded text-sm"
          />
          <div className="flex gap-2">
            <input
              type="text"
              value={newColorValue}
              onChange={(e) => setNewColorValue(e.target.value)}
              placeholder="#000000"
              className="flex-1 px-2 py-1 bg-gray-600 rounded text-sm"
            />
            <input
              type="color"
              value={newColorValue}
              onChange={(e) => setNewColorValue(e.target.value)}
              className="w-12 h-8 bg-gray-600 rounded cursor-pointer"
            />
          </div>
          <select
            value={selectedGroup}
            onChange={(e) => setSelectedGroup(Number(e.target.value))}
            className="w-full px-2 py-1 bg-gray-600 rounded text-sm"
          >
            {paletteStructure.groups.map((group, i) => (
              <option key={i} value={i}>{group.title}</option>
            ))}
          </select>
          <button
            onClick={handleAddColor}
            className="w-full px-3 py-1 bg-green-600 hover:bg-green-700 rounded text-sm"
          >
            Add Color
          </button>
        </div>
      </div>

      {/* Existing Groups and Colors */}
      {paletteStructure.groups.map((group, groupIndex) => (
        <div key={groupIndex} className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <input
              type="text"
              value={group.title}
              onChange={(e) => handleRenameGroup(groupIndex, e.target.value)}
              className="text-lg font-semibold bg-gray-700 px-2 py-1 rounded text-blue-400"
            />
            <button
              onClick={() => handleRemoveGroup(groupIndex)}
              className="text-red-400 hover:text-red-300 text-xs"
            >
              Delete Group
            </button>
          </div>
          <div className="space-y-2">
            {group.colors.map(colorKey => (
              <div key={colorKey} className="flex items-center gap-2">
                <input
                  type="text"
                  value={colorKey}
                  onChange={(e) => handleColorKeyRename(colorKey, e.target.value.replace(/\s/g, ''))}
                  className="w-32 px-2 py-1 bg-gray-700 rounded text-xs text-gray-300"
                />
                <input
                  type="text"
                  value={paletteStructure.colors[colorKey] || '#000000'}
                  onChange={(e) => handleColorChange(colorKey, e.target.value)}
                  className="flex-1 px-2 py-1 bg-gray-700 rounded text-sm"
                  placeholder="#000000"
                />
                <input
                  type="color"
                  value={paletteStructure.colors[colorKey] || '#000000'}
                  onChange={(e) => handleColorChange(colorKey, e.target.value)}
                  className="w-8 h-8 rounded border border-gray-600 cursor-pointer"
                />
                <button
                  onClick={() => handleRemoveColor(colorKey)}
                  className="text-red-400 hover:text-red-300 text-xs px-2"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      ))}
      
      <button
        onClick={onInitialize}
        className="w-full py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold transition-colors"
      >
        Initialize Sphere
      </button>
    </div>
  );
};